import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import remarkCjkFriendly from 'remark-cjk-friendly'

interface MarkdownMessageProps {
  content: string
  className?: string
}

// Renders an AI message as Markdown. The AI often replies with **bold**, bullet or
// numbered lists and headings; without this they show up as raw asterisks and hashes.
// remark-breaks keeps single newlines as line breaks (chat text relies on them);
// remark-gfm adds tables, strikethrough and bare-URL links.
//
// remark-cjk-friendly relaxes CommonMark's flanking rule, which otherwise drops most of
// the AI's bold in Japanese: a closing `**` preceded by punctuation and followed by a
// non-space character cannot close, so `**約38.7%**になる` and `**「…」**ことを` rendered
// as literal asterisks. English never hits this because a space follows the `**`.
// Everything is styled explicitly because the bubbles are small — no prose plugin.
// Rules and table borders use a fixed dark gray rather than currentColor: the AI bubble's
// pale background made a currentColor-tinted border almost invisible.
const MarkdownMessage = ({ content, className = '' }: MarkdownMessageProps) => (
  <div className={`text-left break-words ${className}`}>
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkBreaks, remarkCjkFriendly]}
      components={{
        p: ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
        strong: ({ children }) => <strong className="font-bold">{children}</strong>,
        em: ({ children }) => <em className="italic">{children}</em>,
        del: ({ children }) => <del className="line-through opacity-70">{children}</del>,
        h1: ({ children }) => <h1 className="text-base font-bold mt-3 mb-2 first:mt-0">{children}</h1>,
        h2: ({ children }) => <h2 className="text-base font-bold mt-3 mb-2 first:mt-0">{children}</h2>,
        h3: ({ children }) => <h3 className="text-sm font-bold mt-3 mb-1 first:mt-0">{children}</h3>,
        h4: ({ children }) => <h4 className="text-sm font-bold mt-2 mb-1 first:mt-0">{children}</h4>,
        h5: ({ children }) => <h5 className="text-sm font-bold mt-2 mb-1 first:mt-0">{children}</h5>,
        h6: ({ children }) => <h6 className="text-sm font-bold mt-2 mb-1 first:mt-0">{children}</h6>,
        ul: ({ children }) => <ul className="list-disc pl-5 mb-2 last:mb-0 space-y-1">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 last:mb-0 space-y-1">{children}</ol>,
        li: ({ children }) => <li className="leading-relaxed [&>p]:mb-0">{children}</li>,
        blockquote: ({ children }) => (
          <blockquote className="border-l-2 border-gray-600 pl-3 my-2 opacity-80">{children}</blockquote>
        ),
        hr: () => <hr className="my-3 border-gray-500" />,
        a: ({ href, children }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:opacity-80"
          >
            {children}
          </a>
        ),
        code: ({ className: codeClassName, children }) => {
          // react-markdown gives fenced blocks a `language-*` class; inline code has none.
          const isBlock = /language-/.test(codeClassName || '')
          if (isBlock) {
            return <code className="block font-mono text-xs leading-relaxed">{children}</code>
          }
          return (
            <code className="bg-black/10 rounded px-1 py-0.5 font-mono text-[0.9em]">{children}</code>
          )
        },
        pre: ({ children }) => (
          <pre className="bg-black/10 rounded-md p-2 my-2 overflow-x-auto">{children}</pre>
        ),
        table: ({ children }) => (
          <div className="my-2 overflow-x-auto">
            <table className="min-w-full text-xs border-collapse">{children}</table>
          </div>
        ),
        th: ({ children }) => (
          <th className="border border-gray-600 px-2 py-1 font-bold text-left">{children}</th>
        ),
        td: ({ children }) => <td className="border border-gray-600 px-2 py-1">{children}</td>,
      }}
    >
      {content}
    </ReactMarkdown>
  </div>
)

export default MarkdownMessage
